import React, { useState, useEffect } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import moment from "moment";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableItem,
  DataTableRow,
  PaginationComponent,
  Row,
} from "../../../components/Component";
import { Label, FormGroup, Input, Modal, ModalBody, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import Icon from "../../../components/icon/Icon";
import http from "../../../utils/http-common";
import { API_BASE_URL } from "../../../utils/Utils";
import { isAbortError } from "../../../utils/asyncRequestGuards";
import swal from "sweetalert";
import ConfirmModal from "../../../components/partials/user-manage/ConfirmModal";

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "delivered", label: "Delivered" },
  { value: "qualified", label: "Qualified" },
];

const PartnerLeads = () => {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [detailLead, setDetailLead] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRefreshing, setDetailRefreshing] = useState(false);
  const [editNotes, setEditNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
    status: "new",
    notes: "",
  });
  const [editError, setEditError] = useState("");
  const [editSuccess, setEditSuccess] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [editLeadId, setEditLeadId] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [sendCopyModalOpen, setSendCopyModalOpen] = useState(false);
  const [reassignClinicId, setReassignClinicId] = useState("");
  const [reassignResetStatus, setReassignResetStatus] = useState(true);
  const [sendCopyClinicIds, setSendCopyClinicIds] = useState([]);
  const [reassignSaving, setReassignSaving] = useState(false);
  const [sendCopySaving, setSendCopySaving] = useState(false);
  const [sendCopyResult, setSendCopyResult] = useState(null);
  const [stats, setStats] = useState({ bySource: [], byCampaign: [] });
  const [filters, setFilters] = useState({
    clinicId: "",
    source: "",
    status: "",
    dateFrom: "",
    dateTo: "",
    utm_campaign: "",
  });
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    lead: null,
    loading: false,
  });
  const [deleteError, setDeleteError] = useState("");

  // Multi-clinic assignment states
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignSelectedIds, setAssignSelectedIds] = useState([]);
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignError, setAssignError] = useState("");
  const [removingClinicId, setRemovingClinicId] = useState(null);
  const [updatingClinicStatus, setUpdatingClinicStatus] = useState(null);

  // Edit modal — assigned clinics tag-input state
  const [editPrimaryClinicId, setEditPrimaryClinicId] = useState("");
  const [editAssignedClinics, setEditAssignedClinics] = useState([]);
  const [editAssignedClinicsOriginal, setEditAssignedClinicsOriginal] = useState([]);
  const [editClinicPickValue, setEditClinicPickValue] = useState("");

  // Quick assign panel (from listing row)
  const [quickAssignLead, setQuickAssignLead] = useState(null);
  const [quickAssignOpen, setQuickAssignOpen] = useState(false);
  const [quickPickValue, setQuickPickValue] = useState("");
  const [quickAssigning, setQuickAssigning] = useState(false);
  const [quickRemoving, setQuickRemoving] = useState(null);

  const fetchLeads = (pageNum = 1, signal) => {
    setLoading(true);
    const params = { page: pageNum, limit };
    if (filters.clinicId) params.clinicId = filters.clinicId;
    if (filters.source) params.source = filters.source;
    if (filters.status) params.status = filters.status;
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    if (filters.utm_campaign) params.utm_campaign = filters.utm_campaign;
    http
      .get("/api/v1/leads", { params, signal })
      .then((res) => {
        setLeads(res.data.data || []);
        setTotal(res.data.total ?? res.data.count ?? 0);
        setPage(res.data.page || pageNum);
      })
      .catch((e) => {
        if (isAbortError(e)) return;
        setLeads([]);
        setTotal(0);
      })
      .finally(() => {
        if (!signal?.aborted) setLoading(false);
      });
  };

  useEffect(() => {
    const ac = new AbortController();
    fetchLeads(1, ac.signal);
    return () => ac.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.clinicId, filters.source, filters.status, filters.dateFrom, filters.dateTo, filters.utm_campaign]);

  useEffect(() => {
    const ac = new AbortController();
    http
      .get("/api/v1/partners/medical/clinics", { signal: ac.signal })
      .then((res) => {
        const list = res.data?.data || res.data?.clinics || res.data || [];
        setClinics(Array.isArray(list) ? list : []);
      })
      .catch((e) => {
        if (!isAbortError(e)) setClinics([]);
      });
    return () => ac.abort();
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    const signal = ac.signal;
    const params = {};
    if (filters.clinicId) params.clinicId = filters.clinicId;
    if (filters.source) params.source = filters.source;
    if (filters.status) params.status = filters.status;
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    if (filters.utm_campaign) params.utm_campaign = filters.utm_campaign;
    http
      .get("/api/v1/leads/stats", { params, signal })
      .then((res) => setStats(res.data?.data || { bySource: [], byCampaign: [] }))
      .catch((e) => {
        if (!isAbortError(e)) setStats({ bySource: [], byCampaign: [] });
      });
    return () => ac.abort();
  }, [filters.clinicId, filters.source, filters.status, filters.dateFrom, filters.dateTo, filters.utm_campaign]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleStatusChange = (leadId, newStatus) => {
    http
      .patch(`/api/v1/leads/${leadId}`, { status: newStatus })
      .then(() => {
        setLeads((prev) => prev.map((l) => (l._id === leadId ? { ...l, status: newStatus } : l)));
        if (detailLead && detailLead._id === leadId) {
          return http.get(`/api/v1/leads/${leadId}`).then((res) => {
            const full = res.data?.data;
            if (full) setDetailLead(full);
          });
        }
      })
      .catch(() => {});
  };

  const openDetail = (lead) => {
    setDetailLead(lead);
    setEditNotes(lead?.notes ?? "");
    setDetailOpen(true);
    if (lead?._id) {
      http.get(`/api/v1/leads/${lead._id}`).then((res) => {
        const full = res.data?.data;
        if (full) {
          setDetailLead(full);
          setEditNotes(full.notes ?? "");
        }
      }).catch(() => {});
    }
  };

  const openEdit = (lead) => {
    const source = lead || detailLead;
    if (!source || !source._id) return;
    setEditLeadId(source._id);
    setEditForm({
      firstName: source.firstName ?? "",
      lastName: source.lastName ?? "",
      email: source.email ?? "",
      phone: source.phone ?? "",
      message: source.message ?? "",
      status: source.status ?? "new",
      notes: source.notes ?? "",
    });
    setEditPrimaryClinicId(source.clinicId?._id || source.clinicId || "");
    const initialAssigned = Array.isArray(source.assignedClinics) ? source.assignedClinics : [];
    setEditAssignedClinics(initialAssigned);
    setEditAssignedClinicsOriginal(initialAssigned);
    setEditClinicPickValue("");
    setEditError("");
    setEditSuccess("");
    setEditOpen(true);
  };

  const handleEditFieldChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
    setEditError("");
  };

  const handleSaveEdit = () => {
    if (!editLeadId) return;
    setEditError("");
    setEditSuccess("");
    setSavingEdit(true);

    const payload = {
      firstName: editForm.firstName.trim(),
      lastName: editForm.lastName.trim(),
      email: editForm.email.trim(),
      phone: editForm.phone.trim(),
      message: editForm.message.trim(),
      status: editForm.status,
      notes: editForm.notes.trim() || undefined,
      ...(editPrimaryClinicId ? { clinicId: editPrimaryClinicId } : {}),
    };

    const originalIds = editAssignedClinicsOriginal.map((ac) => ac.clinicId?._id || ac.clinicId).filter(Boolean);
    const currentIds = editAssignedClinics.map((ac) => ac.clinicId?._id || ac.clinicId).filter(Boolean);
    const toAdd = currentIds.filter((id) => !originalIds.includes(id));
    const toRemove = originalIds.filter((id) => !currentIds.includes(id));

    const syncAssignedClinics = () => {
      const addPromise = toAdd.length > 0
        ? http.post(`/api/v1/leads/${editLeadId}/assign-clinics`, { clinicIds: toAdd })
        : Promise.resolve(null);

      const removePromises = toRemove.map((id) =>
        http.delete(`/api/v1/leads/${editLeadId}/assigned-clinics/${id}`).catch(() => null)
      );

      return Promise.all([addPromise, ...removePromises]);
    };

    http
      .patch(`/api/v1/leads/${editLeadId}`, payload)
      .then((res) => {
        const updated = res.data?.data;
        setLeads((prev) =>
          prev.map((l) => (l._id === editLeadId ? { ...l, ...updated } : l))
        );
        return syncAssignedClinics();
      })
      .then(() => {
        setEditSuccess("Lead updated successfully.");
        return http.get(`/api/v1/leads/${editLeadId}`);
      })
      .then((refreshRes) => {
        const full = refreshRes.data?.data;
        if (full) {
          setDetailLead(full);
          setEditNotes(full.notes ?? editForm.notes);
          setLeads((prev) => prev.map((l) => (l._id === editLeadId ? { ...l, ...full } : l)));
          setEditAssignedClinicsOriginal(full.assignedClinics || []);
        }
        setTimeout(() => {
          setEditOpen(false);
          setEditSuccess("");
          setEditLeadId(null);
        }, 600);
      })
      .catch((err) => {
        const msg =
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Failed to update lead.";
        setEditError(msg);
      })
      .finally(() => setSavingEdit(false));
  };

  const handleDeleteClick = (lead) => {
    setDeleteError("");
    setDeleteConfirm({ isOpen: true, lead, loading: false });
  };

  const handleDeleteConfirm = () => {
    const { lead } = deleteConfirm;
    if (!lead?._id) return;
    setDeleteConfirm((prev) => ({ ...prev, loading: true }));
    setDeleteError("");
    http
      .delete(`/api/v1/leads/${lead._id}`)
      .then(() => {
        setDeleteConfirm({ isOpen: false, lead: null, loading: false });
        setDeleteError("");
        if (detailLead && detailLead._id === lead._id) {
          setDetailOpen(false);
          setDetailLead(null);
        }
        fetchLeads(page);
      })
      .catch((err) => {
        const message = err.response?.data?.message || err.message || "Failed to delete lead.";
        setDeleteError(message);
        setDeleteConfirm((prev) => ({ ...prev, loading: false }));
      });
  };

  const activeClinics = clinics.filter((c) => c.status === "active");

  /** Group clinics by partner for Reassign / Send copy: partner label → list of clinics. */
  const clinicsByPartner = React.useMemo(() => {
    const map = new Map();
    activeClinics.forEach((c) => {
      const partnerId = c.partner?._id || c.partnerId || "unknown";
      const partnerLabel = c.partner?.organization_name || "Other";
      if (!map.has(partnerId)) {
        map.set(partnerId, { label: partnerLabel, clinics: [] });
      }
      map.get(partnerId).clinics.push(c);
    });
    return Array.from(map.entries()).map(([id, { label, clinics: list }]) => ({ partnerId: id, label, clinics: list }));
  }, [activeClinics]);

  const openReassignModal = () => {
    setReassignClinicId("");
    setReassignResetStatus(true);
    setReassignModalOpen(true);
  };

  const openSendCopyModal = () => {
    setSendCopyClinicIds([]);
    setSendCopyResult(null);
    setSendCopyModalOpen(true);
  };

  const handleReassignSubmit = () => {
    if (!detailLead?._id || !reassignClinicId) return;
    setReassignSaving(true);
    http
      .post(`/api/v1/leads/${detailLead._id}/reassign`, {
        newClinicId: reassignClinicId,
        resetStatus: reassignResetStatus,
      })
      .then(() => {
        setReassignModalOpen(false);
        return http.get(`/api/v1/leads/${detailLead._id}`);
      })
      .then((res) => {
        const full = res.data?.data;
        if (full) {
          setDetailLead(full);
          setLeads((prev) => prev.map((l) => (l._id === detailLead._id ? { ...l, ...full, clinicId: full.clinicId } : l)));
        }
      })
      .catch((err) => {
        const msg = err.response?.data?.message || "Reassign failed";
        alert(msg);
      })
      .finally(() => setReassignSaving(false));
  };

  const handleSendCopySubmit = () => {
    if (!detailLead?._id || sendCopyClinicIds.length === 0) return;
    setSendCopySaving(true);
    setSendCopyResult(null);
    http
      .post(`/api/v1/leads/${detailLead._id}/send-copy`, { clinicIds: sendCopyClinicIds })
      .then((res) => {
        const created = res.data?.data?.created || [];
        setSendCopyResult(created.length);
        setSendCopySaving(false);
      })
      .catch((err) => {
        const msg = err.response?.data?.message || "Send copy failed";
        alert(msg);
        setSendCopySaving(false);
      });
  };

  const toggleSendCopyClinic = (clinicId) => {
    setSendCopyClinicIds((prev) =>
      prev.includes(clinicId) ? prev.filter((id) => id !== clinicId) : [...prev, clinicId]
    );
  };

  const handleSaveNotes = () => {
    if (!detailLead?._id) return;
    setSavingNotes(true);
    http
      .patch(`/api/v1/leads/${detailLead._id}`, { notes: editNotes })
      .then((res) => {
        const updatedNotes = res.data?.data?.notes ?? editNotes;
        setDetailLead((prev) => (prev ? { ...prev, notes: updatedNotes } : null));
        setEditNotes(updatedNotes ?? "");
        setLeads((prev) =>
          prev.map((l) => (l._id === detailLead._id ? { ...l, notes: updatedNotes } : l))
        );
        return http.get(`/api/v1/leads/${detailLead._id}`);
      })
      .then((res) => {
        const full = res?.data?.data;
        if (full) setDetailLead(full);
      })
      .catch(() => {})
      .finally(() => setSavingNotes(false));
  };

  // ── Multi-clinic assignment handlers ──────────────────────────────────────

  const openAssignModal = () => {
    setAssignSelectedIds([]);
    setAssignError("");
    setAssignModalOpen(true);
  };

  const toggleAssignClinic = (clinicId) => {
    setAssignSelectedIds((prev) =>
      prev.includes(clinicId) ? prev.filter((id) => id !== clinicId) : [...prev, clinicId]
    );
  };

  const handleAssignClinics = () => {
    if (!detailLead?._id || assignSelectedIds.length === 0) return;
    setAssignSaving(true);
    setAssignError("");
    http
      .post(`/api/v1/leads/${detailLead._id}/assign-clinics`, { clinicIds: assignSelectedIds })
      .then((res) => {
        const updated = res.data?.data;
        if (updated) {
          setDetailLead(updated);
          setLeads((prev) => prev.map((l) => (l._id === detailLead._id ? { ...l, assignedClinics: updated.assignedClinics } : l)));
        }
        setAssignModalOpen(false);
      })
      .catch((err) => {
        setAssignError(err.response?.data?.error || "Could not assign clinics. Please try again.");
      })
      .finally(() => setAssignSaving(false));
  };

  const handleRemoveAssignedClinic = (clinicId) => {
    if (!detailLead?._id) return;
    setRemovingClinicId(clinicId);
    http
      .delete(`/api/v1/leads/${detailLead._id}/assigned-clinics/${clinicId}`)
      .then((res) => {
        const updated = res.data?.data;
        if (updated) {
          setDetailLead(updated);
          setLeads((prev) => prev.map((l) => (l._id === detailLead._id ? { ...l, assignedClinics: updated.assignedClinics } : l)));
        }
      })
      .catch(() => {})
      .finally(() => setRemovingClinicId(null));
  };

  const handleUpdateAssignedClinicStatus = (clinicId, newStatus) => {
    if (!detailLead?._id) return;
    setUpdatingClinicStatus(clinicId);
    http
      .patch(`/api/v1/leads/${detailLead._id}/assigned-clinics/${clinicId}`, { status: newStatus })
      .then((res) => {
        const updated = res.data?.data;
        if (updated) {
          setDetailLead(updated);
          setLeads((prev) => prev.map((l) => (l._id === detailLead._id ? { ...l, assignedClinics: updated.assignedClinics } : l)));
        }
      })
      .catch(() => {})
      .finally(() => setUpdatingClinicStatus(null));
  };

  // ── End multi-clinic assignment handlers ──────────────────────────────────

  // ── Quick assign panel (from listing row) ─────────────────────────────────

  const openQuickAssign = (lead) => {
    setQuickAssignLead(lead);
    setQuickPickValue("");
    setQuickAssignOpen(true);
    if (lead?._id) {
      http.get(`/api/v1/leads/${lead._id}`).then((res) => {
        const full = res.data?.data;
        if (full) setQuickAssignLead(full);
      }).catch(() => {});
    }
  };

  const handleQuickAssign = () => {
    if (!quickPickValue || !quickAssignLead?._id) return;
    const clinic = clinics.find((c) => c._id === quickPickValue);
    const clinicLabel = clinic?.name || clinic?.clinicName || quickPickValue;

    swal({
      title: "Notify this partner?",
      text: `Do you want to send a notification to ${clinicLabel} about this lead?`,
      icon: "question",
      buttons: {
        skip: { text: "No, just assign", value: "skip", className: "btn-secondary" },
        notify: { text: "Yes, notify", value: "notify", className: "btn-primary" },
      },
    }).then((choice) => {
      if (choice === null) return;
      setQuickAssigning(true);
      http
        .post(`/api/v1/leads/${quickAssignLead._id}/assign-clinics`, {
          clinicIds: [quickPickValue],
          notify: choice === "notify",
        })
        .then((res) => {
          const updated = res.data?.data;
          if (updated) {
            setQuickAssignLead(updated);
            setLeads((prev) => prev.map((l) => (l._id === quickAssignLead._id ? { ...l, assignedClinics: updated.assignedClinics } : l)));
          }
          setQuickPickValue("");
        })
        .catch(() => {})
        .finally(() => setQuickAssigning(false));
    });
  };

  const handleQuickRemove = (clinicId) => {
    if (!quickAssignLead?._id) return;
    setQuickRemoving(clinicId);
    http
      .delete(`/api/v1/leads/${quickAssignLead._id}/assigned-clinics/${clinicId}`)
      .then((res) => {
        const updated = res.data?.data;
        if (updated) {
          setQuickAssignLead(updated);
          setLeads((prev) => prev.map((l) => (l._id === quickAssignLead._id ? { ...l, assignedClinics: updated.assignedClinics } : l)));
        }
      })
      .catch(() => {})
      .finally(() => setQuickRemoving(null));
  };

  // ── End quick assign panel ─────────────────────────────────────────────────

  const exportCsv = () => {
    const params = { limit: 10000 };
    Object.keys(filters).forEach((k) => { if (filters[k]) params[k] = filters[k]; });
    http.get("/api/v1/leads", { params }).then((res) => {
      const rows = res.data.data || [];
      const headers = [
        "Date", "Source", "Clinic", "Partner", "Preferred country", "First name", "Last name", "Email", "Phone", "Status",
        "utm_source", "utm_medium", "utm_campaign", "utm_content", "landingPath", "Notes",
      ];
      const escape = (v) => (v == null ? "" : String(v).replace(/"/g, '""'));
      const line = (arr) => arr.map((c) => `"${escape(c)}"`).join(",");
      const csv = [line(headers), ...rows.map((l) => line([
        l.createdAt ? moment(l.createdAt).format("YYYY-MM-DD HH:mm") : "",
        l.source || "",
        l.clinicId?.name || l.clinicId?.clinicName || "—",
        l.clinicId?.partner?.organization_name || "—",
        l.preferredCountry || "",
        l.firstName || "",
        l.lastName || "",
        l.email || "",
        l.phone || "",
        l.status || "",
        l.utm_source || "",
        l.utm_medium || "",
        l.utm_campaign || "",
        l.utm_content || "",
        l.landingPath || "",
        l.notes || "",
      ]))].join("\n");
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `partner-leads-${moment().format("YYYY-MM-DD")}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    });
  };

  return (
    <React.Fragment>
      <Head title="Partner Leads" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>Partner Leads</BlockTitle>
              <BlockDes className="text-soft">
                Leads from partner landing pages. Total: {total}.
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button color="light" outline className="me-2" onClick={() => fetchLeads(page)} disabled={loading} title="Refresh list">
                <Icon name="refresh" /> Refresh
              </Button>
              <Button color="primary" onClick={exportCsv} disabled={loading}>
                <Icon name="download" /> Export CSV
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        {(stats.bySource.length > 0 || stats.byCampaign.length > 0) && (
          <Block className="mb-4">
            <BlockHead size="xs">
              <BlockTitle tag="h6">Indicators (filtered)</BlockTitle>
            </BlockHead>
            <Row className="g-3">
              {stats.bySource.length > 0 && (
                <Col md="6">
                  <div className="p-3 bg-light rounded">
                    <strong className="d-block mb-2">By source</strong>
                    <ul className="list-unstyled mb-0 small">
                      {stats.bySource.slice(0, 10).map((s) => (
                        <li key={s.source}><span className="text-soft">{s.source || "—"}</span>: {s.count}</li>
                      ))}
                    </ul>
                  </div>
                </Col>
              )}
              {stats.byCampaign.length > 0 && (
                <Col md="6">
                  <div className="p-3 bg-light rounded">
                    <strong className="d-block mb-2">By campaign (UTM)</strong>
                    <ul className="list-unstyled mb-0 small">
                      {stats.byCampaign.slice(0, 10).map((s) => (
                        <li key={s.utm_campaign}><span className="text-soft">{s.utm_campaign || "—"}</span>: {s.count}</li>
                      ))}
                    </ul>
                  </div>
                </Col>
              )}
            </Row>
          </Block>
        )}

        <Block>
          <div className="admin-list-filters">
            <div className="admin-list-filters__field">
              <Label>Clinic</Label>
              <Input type="select" name="clinicId" value={filters.clinicId} onChange={handleFilterChange}>
                <option value="">All clinics</option>
                {clinics.map((c) => (
                  <option key={c._id} value={c._id}>{c.name || c.clinicName || c._id}</option>
                ))}
              </Input>
            </div>
            <div className="admin-list-filters__field">
              <Label>Source</Label>
              <Input type="text" name="source" value={filters.source} onChange={handleFilterChange} placeholder="Source" />
            </div>
            <div className="admin-list-filters__field">
              <Label>Status</Label>
              <Input type="select" name="status" value={filters.status} onChange={handleFilterChange}>
                <option value="">All</option>
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Input>
            </div>
            <div className="admin-list-filters__field">
              <Label>Campaign (UTM)</Label>
              <Input type="text" name="utm_campaign" value={filters.utm_campaign} onChange={handleFilterChange} placeholder="utm_campaign" />
            </div>
            <div className="admin-list-filters__field">
              <Label>Date from</Label>
              <Input type="date" name="dateFrom" value={filters.dateFrom} onChange={handleFilterChange} />
            </div>
            <div className="admin-list-filters__field">
              <Label>Date to</Label>
              <Input type="date" name="dateTo" value={filters.dateTo} onChange={handleFilterChange} />
            </div>
          </div>

          <DataTable className="card-stretch" scrollable>
            <DataTableBody compact>
              <DataTableHead>
                <DataTableRow><span className="sub-text">Date</span></DataTableRow>
                <DataTableRow><span className="sub-text">Source</span></DataTableRow>
                <DataTableRow><span className="sub-text">Campaign</span></DataTableRow>
                <DataTableRow><span className="sub-text">Clinic / Partner</span></DataTableRow>
                <DataTableRow><span className="sub-text">Contact</span></DataTableRow>
                <DataTableRow><span className="sub-text">Status</span></DataTableRow>
                <DataTableRow className="nk-tb-col-tools text-right"></DataTableRow>
              </DataTableHead>
              {loading ? (
                <DataTableItem>
                  <DataTableRow colSpan={7}><span className="text-soft">Loading…</span></DataTableRow>
                </DataTableItem>
              ) : leads.length === 0 ? (
                <DataTableItem>
                  <DataTableRow colSpan={7}><span className="text-soft">No leads found.</span></DataTableRow>
                </DataTableItem>
              ) : (
                leads.map((item) => (
                  <DataTableItem key={item._id}>
                    <DataTableRow dataLabel="Date">
                      <span>{item.createdAt ? moment(item.createdAt).format("DD/MM/YY HH:mm") : "—"}</span>
                    </DataTableRow>
                    <DataTableRow dataLabel="Source">
                      <span>{item.source || "—"}</span>
                    </DataTableRow>
                    <DataTableRow dataLabel="Campaign">
                      <span>{item.utm_campaign || item.utm_source || "—"}</span>
                    </DataTableRow>
                    <DataTableRow dataLabel="Clinic / Partner">
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                        {/* Primary clinic — clickable tag */}
                        <button
                          type="button"
                          onClick={() => openQuickAssign(item)}
                          title="Click to manage assigned partners"
                          style={{
                            display: "inline-flex", flexDirection: "column", alignItems: "flex-start",
                            background: "#f0f1ff", border: "1px solid #b8bfff", borderRadius: 6,
                            padding: "3px 10px", cursor: "pointer", textAlign: "left",
                          }}
                        >
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#3c4fe0", lineHeight: 1.4 }}>
                            {item.clinicId?.name || item.clinicId?.clinicName || "—"}
                          </span>
                          {item.clinicId?.partner?.organization_name && (
                            <span style={{ fontSize: 11, color: "#8094ae", lineHeight: 1.2 }}>{item.clinicId.partner.organization_name}</span>
                          )}
                        </button>
                        {/* Additional assigned clinics */}
                        {Array.isArray(item.assignedClinics) && item.assignedClinics.slice(0, 2).map((ac) => {
                          const cid = ac.clinicId?._id || ac.clinicId;
                          const c = clinics.find((x) => x._id === cid);
                          const label = ac.clinicId?.name || ac.clinicId?.clinicName || c?.name || c?.clinicName || "Clinic";
                          return (
                            <span key={cid} style={{ fontSize: 11, border: "1px solid #b8bfff", color: "#6576ff", background: "transparent", borderRadius: 4, padding: "2px 6px" }}>
                              {label}
                            </span>
                          );
                        })}
                        {Array.isArray(item.assignedClinics) && item.assignedClinics.length > 2 && (
                          <span style={{ fontSize: 11, borderRadius: 4, padding: "2px 6px", background: "#f5f6fa", color: "#8094ae", border: "1px solid #e5e9f2" }}>
                            +{item.assignedClinics.length - 2}
                          </span>
                        )}
                        {/* Quick add button */}
                        <button
                          type="button"
                          onClick={() => openQuickAssign(item)}
                          title="Add / remove partners"
                          style={{
                            width: 22, height: 22, borderRadius: "50%", border: "1px dashed #b8bfff",
                            background: "none", cursor: "pointer", color: "#6576ff",
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            fontSize: 16, lineHeight: 1, padding: 0,
                          }}
                          aria-label="Manage assigned partners"
                        >+</button>
                      </div>
                    </DataTableRow>
                    <DataTableRow dataLabel="Contact">
                      <span>{[item.firstName, item.lastName].filter(Boolean).join(" ")}</span>
                      <small className="text-soft d-block">{item.email}</small>
                      {item.preferredCountry && (
                        <small className="text-soft d-block">Country (client): {item.preferredCountry}</small>
                      )}
                    </DataTableRow>
                    <DataTableRow dataLabel="Status">
                      <UncontrolledDropdown>
                        <DropdownToggle tag="span" className="dropdown-toggle btn btn-sm btn-outline-light">
                          {item.status || "new"}
                        </DropdownToggle>
                        <DropdownMenu>
                          {STATUS_OPTIONS.map((o) => (
                            <DropdownItem key={o.value} onClick={() => handleStatusChange(item._id, o.value)}>
                              {o.label}
                            </DropdownItem>
                          ))}
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </DataTableRow>
                    <DataTableRow className="nk-tb-col-tools" dataLabel="Actions">
                      <Button color="light" size="sm" onClick={() => openDetail(item)} className="me-1">
                        <Icon name="eye" /> View
                      </Button>
                      <Button color="primary" size="sm" onClick={() => openEdit(item)} className="me-1" aria-label="Edit lead">
                        <Icon name="edit" /> Edit
                      </Button>
                      <Button color="light" size="sm" className="text-danger" onClick={() => handleDeleteClick(item)} title="Delete">
                        <Icon name="trash" /> Delete
                      </Button>
                    </DataTableRow>
                  </DataTableItem>
                ))
              )}
            </DataTableBody>
            <div className="card-inner">
              {!loading && leads.length > 0 && (
                <PaginationComponent
                  itemPerPage={limit}
                  totalItems={total}
                  paginate={(p) => fetchLeads(p)}
                  currentPage={page}
                />
              )}
            </div>
          </DataTable>
        </Block>

        <Modal isOpen={detailOpen} toggle={() => setDetailOpen(false)} className="modal-dialog-centered" size="lg">
          <ModalBody>
            <a href="#close" onClick={(e) => { e.preventDefault(); setDetailOpen(false); }} className="close">
              <Icon name="cross-sm" />
            </a>
            <div className="d-flex justify-content-between align-items-center flex-wrap flex-g-2">
              <h5 className="title mb-0">Lead details</h5>
              {detailLead && (
                <Button color="primary" size="sm" onClick={() => openEdit(detailLead)} aria-label="Edit this lead">
                  <Icon name="edit" /> Edit
                </Button>
              )}
            </div>
            {detailLead && (
              <div className="mt-4">
                <div className="d-flex flex-g-2 mb-3 flex-wrap">
                  <Button color="secondary" size="sm" onClick={openReassignModal}>
                    <Icon name="arrow-right" /> Reassign to clinic
                  </Button>
                  <Button color="secondary" size="sm" onClick={openSendCopyModal}>
                    <Icon name="copy" /> Send copy to partner(s)
                  </Button>
                </div>
                <Row className="gy-3">
                  <Col md="6"><strong>Date</strong><p className="mb-0">{detailLead.createdAt ? moment(detailLead.createdAt).format("DD/MM/YYYY HH:mm") : "—"}</p></Col>
                  <Col md="6"><strong>Status</strong><p className="mb-0">{detailLead.status || "new"}</p></Col>
                  <Col md="6"><strong>Source</strong><p className="mb-0">{detailLead.source || "—"}</p></Col>
                  <Col md="6"><strong>Campaign (UTM)</strong><p className="mb-0">{detailLead.utm_campaign || detailLead.utm_source || "—"}</p></Col>
                  <Col md="12"><strong>Landing path</strong><p className="mb-0 text-break">{detailLead.landingPath || "—"}</p></Col>
                  <Col md="6"><strong>utm_source</strong><p className="mb-0">{detailLead.utm_source || "—"}</p></Col>
                  <Col md="6"><strong>utm_medium</strong><p className="mb-0">{detailLead.utm_medium || "—"}</p></Col>
                  <Col md="6"><strong>utm_campaign</strong><p className="mb-0">{detailLead.utm_campaign || "—"}</p></Col>
                  <Col md="6"><strong>utm_content</strong><p className="mb-0">{detailLead.utm_content || "—"}</p></Col>
                  <Col md="6"><strong>Clinic</strong><p className="mb-0">{detailLead.clinicId?.name || detailLead.clinicId?.clinicName || "—"}</p></Col>
                  <Col md="6"><strong>Partner</strong><p className="mb-0">{detailLead.clinicId?.partner?.organization_name || "—"}</p></Col>
                  <Col md="6"><strong>Preferred country (client)</strong><p className="mb-0">{detailLead.preferredCountry || "—"}</p></Col>
                  <Col md="6"><strong>First name</strong><p className="mb-0">{detailLead.firstName}</p></Col>
                  <Col md="6"><strong>Last name</strong><p className="mb-0">{detailLead.lastName}</p></Col>
                  <Col md="6"><strong>Email</strong><p className="mb-0"><a href={`mailto:${detailLead.email}`}>{detailLead.email}</a></p></Col>
                  <Col md="6"><strong>Phone</strong><p className="mb-0"><a href={`tel:${detailLead.phone}`}>{detailLead.phone}</a></p></Col>
                  <Col md="12">
                    <strong className="d-block mb-1">Treatment / preferences (thank-you page)</strong>
                    <p className="small text-soft mb-1">Filled when the client submits the thank-you form (treatment choice + details). Click Refresh to load the latest.</p>
                    <div className="p-2 rounded bg-light" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {detailLead.message && detailLead.message.trim() ? detailLead.message : "— Not filled yet"}
                    </div>
                  </Col>
                  <Col md="12">
                    <hr className="my-3" />
                    <div className="d-flex align-items-center flex-g-2 mb-2">
                      <strong className="d-block mb-0">Photos / attachments ({Array.isArray(detailLead.attachments) ? detailLead.attachments.length : 0})</strong>
                      <Button
                        color="light"
                        size="sm"
                        outline
                        disabled={detailRefreshing}
                        onClick={() => {
                          if (!detailLead?._id) return;
                          setDetailRefreshing(true);
                          http.get(`/api/v1/leads/${detailLead._id}`).then((res) => {
                            const full = res.data?.data;
                            if (full) setDetailLead(full);
                          }).catch(() => {}).finally(() => setDetailRefreshing(false));
                        }}
                        title="Refresh to see new uploads"
                      >
                        {detailRefreshing ? "…" : "Refresh"}
                      </Button>
                    </div>
                    <p className="text-soft small mb-2">Files uploaded by the client on the thank-you page. To see the latest treatment choice and photos, click Refresh.</p>
                    {(Array.isArray(detailLead.attachments) ? detailLead.attachments : []).length > 0 ? (
                      <div className="d-flex flex-wrap flex-g-2">
                        {(detailLead.attachments || []).map((att, idx) => {
                          const base = (API_BASE_URL || "").replace(/\/$/, "");
                          const url = att.path ? `${base}/uploads/${att.path}` : "";
                          const isImage = (att.mimeType || "").startsWith("image/");
                          return (
                            <div key={att.id || att._id || `att-${idx}`} className="border rounded p-2" style={{ maxWidth: 160 }}>
                              {isImage && url ? (
                                <a href={url} target="_blank" rel="noopener noreferrer" className="d-block mb-1">
                                  <img src={url} alt={att.originalName || "Attachment"} style={{ maxWidth: "100%", height: 100, objectFit: "cover" }} onError={(e) => { e.target.style.display = "none"; }} />
                                </a>
                              ) : null}
                              <a href={url} target="_blank" rel="noopener noreferrer" className="small text-break d-block">
                                {att.originalName || "File"}
                              </a>
                              {att.size ? <small className="text-soft">{Math.round(att.size / 1024)} KB</small> : null}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-soft small mb-0">No photos yet. The client can upload on the thank-you page. Click Refresh after they upload.</p>
                    )}
                  </Col>
                  <Col md="12">
                    <FormGroup>
                      <Label for="lead-notes">Notes</Label>
                      <Input
                        id="lead-notes"
                        type="textarea"
                        rows={4}
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        placeholder="Internal notes (follow-up, context…)"
                        maxLength={5000}
                        className="mb-2"
                      />
                      <Button color="primary" size="sm" onClick={handleSaveNotes} disabled={savingNotes} className="me-2">
                        {savingNotes ? "Saving…" : "Save notes"}
                      </Button>
                      <Button color="danger" size="sm" outline onClick={() => { setDetailOpen(false); handleDeleteClick(detailLead); }}>
                        <Icon name="trash" /> Delete
                      </Button>
                    </FormGroup>
                  </Col>
                  {/* ── Assigned Clinics ───────────────────────────────── */}
                  <Col md="12">
                    <hr className="my-3" />
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>Assigned clinics ({Array.isArray(detailLead.assignedClinics) ? detailLead.assignedClinics.length : 0})</strong>
                      <Button color="primary" size="sm" outline onClick={openAssignModal}>
                        <Icon name="plus" /> Add
                      </Button>
                    </div>
                    {Array.isArray(detailLead.assignedClinics) && detailLead.assignedClinics.length > 0 ? (
                      <ul className="list-unstyled mb-0">
                        {detailLead.assignedClinics.map((ac) => {
                          const clinic = clinics.find((c) => c._id === (ac.clinicId?._id || ac.clinicId));
                          const clinicName = ac.clinicId?.name || ac.clinicId?.clinicName || clinic?.name || clinic?.clinicName || "—";
                          const partnerName = ac.clinicId?.partner?.organization_name || clinic?.partner?.organization_name;
                          const clinicObjectId = ac.clinicId?._id || ac.clinicId;
                          const isRemoving = removingClinicId === clinicObjectId;
                          const isUpdating = updatingClinicStatus === clinicObjectId;
                          return (
                            <li key={clinicObjectId} className="d-flex align-items-center justify-content-between py-2 border-bottom border-light flex-g-2">
                              <div>
                                <span className="fw-medium">{clinicName}</span>
                                {partnerName && <small className="text-soft d-block">{partnerName}</small>}
                                <small className="text-soft d-block">Assigned {ac.assignedAt ? moment(ac.assignedAt).format("DD/MM/YYYY") : "—"}</small>
                              </div>
                              <div className="d-flex align-items-center flex-g-2">
                                <Input
                                  type="select"
                                  bsSize="sm"
                                  value={ac.status || "pending"}
                                  disabled={isUpdating}
                                  onChange={(e) => handleUpdateAssignedClinicStatus(clinicObjectId, e.target.value)}
                                  style={{ width: 130 }}
                                  aria-label={`Status for ${clinicName}`}
                                >
                                  <option value="pending">Pending</option>
                                  <option value="contacted">Contacted</option>
                                  <option value="interested">Interested</option>
                                  <option value="converted">Converted</option>
                                  <option value="rejected">Rejected</option>
                                </Input>
                                <Button
                                  color="danger"
                                  size="sm"
                                  outline
                                  disabled={isRemoving}
                                  onClick={() => handleRemoveAssignedClinic(clinicObjectId)}
                                  aria-label={`Remove ${clinicName}`}
                                >
                                  {isRemoving ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> : <Icon name="trash" />}
                                </Button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p className="text-soft small mb-0">No clinic assigned yet. Click "Add" to assign one or more clinics.</p>
                    )}
                  </Col>
                  {/* ── End Assigned Clinics ───────────────────────────── */}

                  {Array.isArray(detailLead.history) && detailLead.history.length > 0 && (
                    <Col md="12">
                      <hr className="my-3" />
                      <strong className="d-block mb-2">History</strong>
                      <p className="text-soft small mb-2">Changes (notes, status) with who and when.</p>
                      <ul className="list-unstyled mb-0">
                        {detailLead.history.map((entry, idx) => {
                          const userLabel = entry.userId
                            ? [entry.userId.first_name, entry.userId.last_name].filter(Boolean).join(" ") || entry.userId.email
                            : entry.userRole || "—";
                          const typeLabel =
                            entry.type === "note_updated"
                              ? "Notes"
                              : entry.type === "status_updated"
                              ? "Status"
                              : entry.type === "reassigned"
                              ? "Reassigned"
                              : entry.type === "lead_updated"
                              ? "Lead updated"
                              : entry.type || "—";
                          return (
                            <li key={entry._id || idx} className="mb-3 pb-3 border-bottom border-light">
                              <span className="text-soft small d-block">
                                {entry.timestamp ? moment(entry.timestamp).format("DD/MM/YYYY HH:mm") : "—"} · {userLabel} · {typeLabel}
                              </span>
                              {(entry.oldValue != null || entry.newValue != null) && (
                                <div className="small mt-1">
                                  {entry.type === "status_updated" && (
                                    <span>
                                      <span className="text-soft">{entry.oldValue || "—"}</span>
                                      <span className="mx-1">→</span>
                                      <span>{entry.newValue || "—"}</span>
                                    </span>
                                  )}
                                  {entry.type === "note_updated" && (
                                    <div>
                                      <div className="text-soft">Previous: {entry.oldValue ? `${String(entry.oldValue).slice(0, 200)}${String(entry.oldValue).length > 200 ? "…" : ""}` : "—"}</div>
                                      <div>New: {entry.newValue ? `${String(entry.newValue).slice(0, 200)}${String(entry.newValue).length > 200 ? "…" : ""}` : "—"}</div>
                                    </div>
                                  )}
                                  {entry.type === "reassigned" && (
                                    <span>
                                      <span className="text-soft">{entry.oldValue || "—"}</span>
                                      <span className="mx-1">→</span>
                                      <span>{entry.newValue || "—"}</span>
                                    </span>
                                  )}
                                  {entry.type === "lead_updated" && (
                                    <span>
                                      <span className="text-soft">{entry.oldValue || "—"}</span>
                                      <span className="mx-1">→</span>
                                      <span>{entry.newValue || "—"}</span>
                                    </span>
                                  )}
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </Col>
                  )}
                </Row>
              </div>
            )}
          </ModalBody>
        </Modal>

        <Modal
          isOpen={editOpen}
          toggle={() => { setEditOpen(false); setEditError(""); setEditSuccess(""); }}
          className="modal-dialog-centered"
          size="lg"
          scrollable
          aria-labelledby="edit-lead-modal-title"
          aria-describedby="edit-lead-modal-desc"
        >
          <ModalBody>
            <a href="#close" onClick={(e) => { e.preventDefault(); setEditOpen(false); setEditError(""); setEditSuccess(""); }} className="close" aria-label="Close edit form">
              <Icon name="cross-sm" />
            </a>
            <h5 id="edit-lead-modal-title" className="title">Edit lead</h5>
            <p id="edit-lead-modal-desc" className="visually-hidden">Edit contact and status for this lead. All fields are optional to change.</p>
            {editSuccess && <p className="text-success small mb-2" role="status">{editSuccess}</p>}
            {editError && <p className="text-danger small mb-2" role="alert">{editError}</p>}
            <Row className="gy-3 mt-2">
              <Col md="6">
                <FormGroup>
                  <Label for="edit-firstName">First name</Label>
                  <Input id="edit-firstName" name="firstName" value={editForm.firstName} onChange={handleEditFieldChange} placeholder="First name" maxLength={100} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="edit-lastName">Last name</Label>
                  <Input id="edit-lastName" name="lastName" value={editForm.lastName} onChange={handleEditFieldChange} placeholder="Last name" maxLength={100} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="edit-email">Email</Label>
                  <Input id="edit-email" name="email" type="email" value={editForm.email} onChange={handleEditFieldChange} placeholder="Email" />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="edit-phone">Phone</Label>
                  <Input id="edit-phone" name="phone" value={editForm.phone} onChange={handleEditFieldChange} placeholder="Phone" maxLength={50} />
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <Label for="edit-message">Message</Label>
                  <Input id="edit-message" name="message" type="textarea" rows={3} value={editForm.message} onChange={handleEditFieldChange} placeholder="Message" maxLength={2000} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="edit-status">Status</Label>
                  <Input id="edit-status" name="status" type="select" value={editForm.status} onChange={handleEditFieldChange}>
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <Label for="edit-notes">Notes</Label>
                  <Input id="edit-notes" name="notes" type="textarea" rows={4} value={editForm.notes} onChange={handleEditFieldChange} placeholder="Internal notes" maxLength={5000} />
                </FormGroup>
              </Col>

              {/* ── Partner Assignment ───────────────────────────────── */}
              <Col md="12">
                <hr className="my-1" />
                <h6 className="overline-title mb-3" style={{ color: "#6576ff" }}>Partner assignment</h6>
              </Col>

              {/* Primary clinic — fully editable select */}
              <Col md="12">
                <FormGroup>
                  <Label for="edit-primary-clinic">
                    Primary clinic
                    <small className="text-soft ms-1">(main assignment for this lead)</small>
                  </Label>
                  <Input
                    id="edit-primary-clinic"
                    type="select"
                    value={editPrimaryClinicId}
                    onChange={(e) => setEditPrimaryClinicId(e.target.value)}
                  >
                    <option value="">— No primary clinic —</option>
                    {clinics.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.name || c.clinicName || c._id}
                        {c.partner?.organization_name ? ` · ${c.partner.organization_name}` : ""}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>

              {/* Additional assigned clinics — removable tags */}
              <Col md="12">
                <FormGroup>
                  <Label>
                    Additional assigned clinics
                    <small className="text-soft ms-1">(will also receive this lead)</small>
                  </Label>
                  <div
                    style={{
                      display: "flex", flexWrap: "wrap", gap: 6, minHeight: 40,
                      border: "1px solid #dbdfea", borderRadius: 4, padding: "6px 8px",
                      background: "#fafbff", alignItems: "flex-start",
                    }}
                  >
                    {editAssignedClinics.length === 0 && (
                      <span className="text-soft" style={{ fontSize: 13, lineHeight: "26px" }}>None — add clinics below</span>
                    )}
                    {editAssignedClinics.map((ac) => {
                      const cid = ac.clinicId?._id || ac.clinicId;
                      const c = clinics.find((x) => x._id === cid);
                      const label = ac.clinicId?.name || ac.clinicId?.clinicName || c?.name || c?.clinicName || cid;
                      const partner = ac.clinicId?.partner?.organization_name || c?.partner?.organization_name;
                      return (
                        <span
                          key={cid}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 5,
                            padding: "3px 8px 3px 10px", borderRadius: 20, fontSize: 13,
                            background: "#e8eaff", color: "#3c4fe0", border: "1px solid #b8bfff",
                            lineHeight: 1.6,
                          }}
                        >
                          <span>
                            {label}
                            {partner && <span style={{ opacity: 0.65, fontSize: 11 }}> · {partner}</span>}
                          </span>
                          <button
                            type="button"
                            onClick={() => setEditAssignedClinics((prev) => prev.filter((x) => (x.clinicId?._id || x.clinicId) !== cid))}
                            aria-label={`Remove ${label}`}
                            style={{
                              background: "none", border: "none", cursor: "pointer",
                              color: "#3c4fe0", padding: "0 0 0 2px", lineHeight: 1, fontWeight: 700, fontSize: 15,
                            }}
                          >×</button>
                        </span>
                      );
                    })}
                  </div>

                  {/* Add clinic picker */}
                  <div className="d-flex flex-g-2 mt-2">
                    <Input
                      type="select"
                      bsSize="sm"
                      value={editClinicPickValue}
                      onChange={(e) => setEditClinicPickValue(e.target.value)}
                      style={{ flex: 1 }}
                      aria-label="Pick a clinic to add"
                    >
                      <option value="">+ Add a clinic…</option>
                      {clinics
                        .filter((c) => {
                          const alreadyAssigned = editAssignedClinics.some((ac) => (ac.clinicId?._id || ac.clinicId) === c._id);
                          const isPrimary = c._id === editPrimaryClinicId;
                          return !alreadyAssigned && !isPrimary;
                        })
                        .map((c) => (
                          <option key={c._id} value={c._id}>
                            {c.name || c.clinicName || c._id}
                            {c.partner?.organization_name ? ` · ${c.partner.organization_name}` : ""}
                          </option>
                        ))}
                    </Input>
                    <Button
                      color="primary"
                      size="sm"
                      outline
                      disabled={!editClinicPickValue}
                      onClick={() => {
                        if (!editClinicPickValue) return;
                        setEditAssignedClinics((prev) => [
                          ...prev,
                          { clinicId: editClinicPickValue, status: "pending", assignedAt: new Date().toISOString() },
                        ]);
                        setEditClinicPickValue("");
                      }}
                    >
                      Add
                    </Button>
                  </div>
                </FormGroup>
              </Col>
              {/* ── End Partner Assignment ───────────────────────────── */}

              <Col md="12">
                <Button color="primary" onClick={handleSaveEdit} disabled={savingEdit}>
                  {savingEdit ? "Saving…" : "Save"}
                </Button>
                <Button color="light" className="ms-2" onClick={() => { setEditOpen(false); setEditError(""); setEditSuccess(""); }}>
                  Cancel
                </Button>
              </Col>
            </Row>
          </ModalBody>
        </Modal>

        <Modal isOpen={reassignModalOpen} toggle={() => setReassignModalOpen(false)} className="modal-dialog-centered">
          <ModalBody>
            <a href="#close" onClick={(e) => { e.preventDefault(); setReassignModalOpen(false); }} className="close">
              <Icon name="cross-sm" />
            </a>
            <h5 className="title">Reassign to clinic</h5>
            <p className="text-soft small">Choose the clinic this lead will be assigned to. The new clinic will be notified.</p>
            <FormGroup className="mt-3">
              <Label for="reassign-clinic">Clinic</Label>
              <Input
                id="reassign-clinic"
                type="select"
                value={reassignClinicId}
                onChange={(e) => setReassignClinicId(e.target.value)}
              >
                <option value="">Select a clinic</option>
                {activeClinics
                  .filter((c) => c._id !== detailLead?.clinicId?._id && c._id !== detailLead?.clinicId)
                  .map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name || c.clinicName || c._id}
                      {c.partner?.organization_name ? ` (${c.partner.organization_name})` : ""}
                    </option>
                  ))}
              </Input>
            </FormGroup>
            <FormGroup check className="mt-2">
              <Label check>
                <Input
                  type="checkbox"
                  checked={reassignResetStatus}
                  onChange={(e) => setReassignResetStatus(e.target.checked)}
                />
                Reset status to New
              </Label>
            </FormGroup>
            <div className="d-flex flex-g-2 mt-4">
              <Button color="primary" onClick={handleReassignSubmit} disabled={!reassignClinicId || reassignSaving}>
                {reassignSaving ? "Reassigning…" : "Confirm"}
              </Button>
              <Button color="light" onClick={() => setReassignModalOpen(false)}>
                Cancel
              </Button>
            </div>
          </ModalBody>
        </Modal>

        <Modal isOpen={sendCopyModalOpen} toggle={() => setSendCopyModalOpen(false)} className="modal-dialog-centered">
          <ModalBody>
            <a href="#close" onClick={(e) => { e.preventDefault(); setSendCopyModalOpen(false); }} className="close">
              <Icon name="cross-sm" />
            </a>
            <h5 className="title">Send copy to partner(s)</h5>
            {sendCopyResult !== null ? (
              <div className="mt-3">
                <p className="text-success mb-0">Copy sent to {sendCopyResult} partner(s).</p>
                <Button color="primary" className="mt-3" onClick={() => { setSendCopyModalOpen(false); setSendCopyResult(null); }}>
                  Close
                </Button>
              </div>
            ) : (
              <>
                <p className="text-soft small">Select one or more clinics to receive a copy of this lead, grouped by partner. Each will get a new lead with the same contact data.</p>
                <FormGroup className="mt-3">
                  <Label>Clinics by partner</Label>
                  <div className="border rounded p-2" style={{ maxHeight: 260, overflowY: "auto" }}>
                    {clinicsByPartner.map(({ label: partnerLabel, clinics: list }) => {
                      const filtered = list.filter(
                        (c) => c._id !== detailLead?.clinicId?._id && c._id !== detailLead?.clinicId
                      );
                      if (filtered.length === 0) return null;
                      return (
                        <div key={partnerLabel} className="mb-3">
                          <strong className="d-block mb-2 small text-soft">{partnerLabel}</strong>
                          {filtered.map((c) => (
                            <FormGroup check key={c._id}>
                              <Label check>
                                <Input
                                  type="checkbox"
                                  checked={sendCopyClinicIds.includes(c._id)}
                                  onChange={() => toggleSendCopyClinic(c._id)}
                                />
                                {c.name || c.clinicName || c._id}
                              </Label>
                            </FormGroup>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </FormGroup>
                <div className="d-flex flex-g-2 mt-4">
                  <Button color="primary" onClick={handleSendCopySubmit} disabled={sendCopyClinicIds.length === 0 || sendCopySaving}>
                    {sendCopySaving ? "Sending…" : "Confirm"}
                  </Button>
                  <Button color="light" onClick={() => setSendCopyModalOpen(false)}>
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </ModalBody>
        </Modal>

        {/* ── Assign Clinics Modal ───────────────────────────────────────── */}
        <Modal isOpen={assignModalOpen} toggle={() => setAssignModalOpen(false)} className="modal-dialog-centered" size="lg">
          <ModalBody>
            <a href="#close" onClick={(e) => { e.preventDefault(); setAssignModalOpen(false); }} className="close" aria-label="Close">
              <Icon name="cross-sm" />
            </a>
            <h5 className="title mb-1">Assign clinics to this lead</h5>
            <p className="text-soft small mb-3">Select one or more clinics. Already-assigned clinics are excluded.</p>
            {assignError && <div className="alert alert-danger py-2 small mb-3" role="alert">{assignError}</div>}
            <div style={{ maxHeight: 380, overflowY: "auto" }} className="border rounded p-2">
              {clinics
                .filter((c) => {
                  const alreadyAssigned = Array.isArray(detailLead?.assignedClinics)
                    && detailLead.assignedClinics.some((ac) => (ac.clinicId?._id || ac.clinicId) === c._id);
                  return !alreadyAssigned;
                })
                .map((c) => (
                  <label key={c._id} className="d-flex align-items-center flex-g-2 py-2 border-bottom border-light cursor-pointer" style={{ cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={assignSelectedIds.includes(c._id)}
                      onChange={() => toggleAssignClinic(c._id)}
                      className="me-1"
                    />
                    <span>
                      <span className="fw-medium">{c.name || c.clinicName || c._id}</span>
                      {c.partner?.organization_name && (
                        <small className="text-soft ms-2">({c.partner.organization_name})</small>
                      )}
                    </span>
                  </label>
                ))}
              {clinics.filter((c) => {
                const alreadyAssigned = Array.isArray(detailLead?.assignedClinics)
                  && detailLead.assignedClinics.some((ac) => (ac.clinicId?._id || ac.clinicId) === c._id);
                return !alreadyAssigned;
              }).length === 0 && (
                <p className="text-soft small mb-0 p-2">All available clinics are already assigned to this lead.</p>
              )}
            </div>
            <div className="d-flex flex-g-2 mt-3">
              <Button
                color="primary"
                onClick={handleAssignClinics}
                disabled={assignSaving || assignSelectedIds.length === 0}
              >
                {assignSaving
                  ? <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />Saving…</>
                  : `Assign ${assignSelectedIds.length > 0 ? `(${assignSelectedIds.length})` : ""}`
                }
              </Button>
              <Button color="light" onClick={() => setAssignModalOpen(false)}>Cancel</Button>
            </div>
          </ModalBody>
        </Modal>
        {/* ── End Assign Clinics Modal ───────────────────────────────────── */}

        {/* ── Quick Assign Panel (from listing row) ─────────────────────── */}
        <Modal isOpen={quickAssignOpen} toggle={() => setQuickAssignOpen(false)} className="modal-dialog-centered" size="md">
          <ModalBody>
            <a href="#close" onClick={(e) => { e.preventDefault(); setQuickAssignOpen(false); }} className="close" aria-label="Close">
              <Icon name="cross-sm" />
            </a>
            <h5 className="title mb-0">Assign partners</h5>
            {quickAssignLead && (
              <p className="text-soft small mb-3">
                Lead: <strong>{[quickAssignLead.firstName, quickAssignLead.lastName].filter(Boolean).join(" ") || quickAssignLead.email}</strong>
              </p>
            )}

            {/* Primary clinic — non-removable tag */}
            {quickAssignLead?.clinicId && (
              <div className="mb-3">
                <small className="text-soft d-block mb-1 text-uppercase" style={{ fontSize: 10, letterSpacing: "0.06em" }}>Primary clinic</small>
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "4px 12px", borderRadius: 20, fontSize: 13,
                  background: "#6576ff", color: "#fff", fontWeight: 600,
                }}>
                  {quickAssignLead.clinicId?.name || quickAssignLead.clinicId?.clinicName || "—"}
                  {quickAssignLead.clinicId?.partner?.organization_name && (
                    <span style={{ opacity: 0.8, fontSize: 11 }}>· {quickAssignLead.clinicId.partner.organization_name}</span>
                  )}
                </span>
              </div>
            )}

            {/* Assigned clinics — removable tags */}
            <small className="text-soft d-block mb-2 text-uppercase" style={{ fontSize: 10, letterSpacing: "0.06em" }}>Additional partners</small>
            <div style={{
              display: "flex", flexWrap: "wrap", gap: 6, minHeight: 42,
              border: "1px solid #dbdfea", borderRadius: 6, padding: "8px 10px",
              background: "#fafbff", alignItems: "flex-start", marginBottom: 12,
            }}>
              {(!Array.isArray(quickAssignLead?.assignedClinics) || quickAssignLead.assignedClinics.length === 0) && (
                <span className="text-soft" style={{ fontSize: 13, lineHeight: "26px" }}>None yet</span>
              )}
              {Array.isArray(quickAssignLead?.assignedClinics) && quickAssignLead.assignedClinics.map((ac) => {
                const cid = ac.clinicId?._id || ac.clinicId;
                const c = clinics.find((x) => x._id === cid);
                const label = ac.clinicId?.name || ac.clinicId?.clinicName || c?.name || c?.clinicName || cid;
                const partner = ac.clinicId?.partner?.organization_name || c?.partner?.organization_name;
                const isRemoving = quickRemoving === cid;
                return (
                  <span key={cid} style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    padding: "4px 8px 4px 11px", borderRadius: 20, fontSize: 13,
                    background: "#e8eaff", color: "#3c4fe0", border: "1px solid #b8bfff",
                  }}>
                    <span>
                      {label}
                      {partner && <span style={{ opacity: 0.65, fontSize: 11 }}> · {partner}</span>}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleQuickRemove(cid)}
                      disabled={isRemoving}
                      aria-label={`Remove ${label}`}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#3c4fe0", padding: "0 0 0 2px", lineHeight: 1, fontWeight: 700, fontSize: 15 }}
                    >
                      {isRemoving ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} role="status" aria-hidden="true" /> : "×"}
                    </button>
                  </span>
                );
              })}
            </div>

            {/* Add clinic */}
            <small className="text-soft d-block mb-2 text-uppercase" style={{ fontSize: 10, letterSpacing: "0.06em" }}>Add a partner clinic</small>
            <div className="d-flex flex-g-2">
              <Input
                type="select"
                bsSize="sm"
                value={quickPickValue}
                onChange={(e) => setQuickPickValue(e.target.value)}
                style={{ flex: 1 }}
                aria-label="Select clinic to assign"
              >
                <option value="">Select a clinic…</option>
                {clinics
                  .filter((c) => {
                    const isPrimary = c._id === (quickAssignLead?.clinicId?._id || quickAssignLead?.clinicId);
                    const alreadyAssigned = Array.isArray(quickAssignLead?.assignedClinics)
                      && quickAssignLead.assignedClinics.some((ac) => (ac.clinicId?._id || ac.clinicId) === c._id);
                    return !isPrimary && !alreadyAssigned;
                  })
                  .map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name || c.clinicName || c._id}
                      {c.partner?.organization_name ? ` · ${c.partner.organization_name}` : ""}
                    </option>
                  ))}
              </Input>
              <Button
                color="primary"
                size="sm"
                disabled={!quickPickValue || quickAssigning}
                onClick={handleQuickAssign}
              >
                {quickAssigning
                  ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  : "Assign"
                }
              </Button>
            </div>

            <div className="mt-4 text-right">
              <Button color="light" size="sm" onClick={() => setQuickAssignOpen(false)}>Done</Button>
            </div>
          </ModalBody>
        </Modal>
        {/* ── End Quick Assign Panel ─────────────────────────────────────── */}

        <ConfirmModal
          isOpen={deleteConfirm.isOpen}
          onClose={() => { setDeleteConfirm({ isOpen: false, lead: null, loading: false }); setDeleteError(""); }}
          onConfirm={handleDeleteConfirm}
          title="Delete lead"
          message={
            deleteConfirm.lead
              ? `Are you sure you want to delete this lead (${deleteConfirm.lead.firstName || ""} ${deleteConfirm.lead.lastName || ""} – ${deleteConfirm.lead.email || ""})? This action cannot be undone.`
              : ""
          }
          confirmText="Delete"
          type="danger"
          loading={deleteConfirm.loading}
        />
        {deleteError && (
          <div className="alert alert-danger mt-3 mb-0" role="alert">
            {deleteError}
          </div>
        )}
      </Content>
    </React.Fragment>
  );
};

export default PartnerLeads;
